import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { APIService } from '../api/api.service';
import { KPI } from '../api/models';
import { PerimetersService } from './perimeters.service';
import * as R from 'ramda';

export interface EnergyObjective {
  id: number | string;
  site_id: number;
  building_id: number;
  year: number;
  consumption: number;
  emission: number;
  site_name?: string;
  building_name?: string;
  surface?: number;
  objectiveType?: string;
  importDate?: string;
  building?: string;
  objectiveValue?: number;
}

export interface EnergyObjectiveCreatePayload {
  site_id: number;
  sub_perimeter_id: number;
  year: number;
  consumption: number; 
  energy_objective_type?: string;
  declared?: boolean;
}

export interface EnergyObjectiveUpdatePayload {
  site_id: number;
  sub_perimeter_id: number; 
  year: number;
  consumption: number; 
  energy_objective_type?: string;
  declared?: boolean;
}

export interface CarbonObjective {
  id: number | string;
  site_id: number;
  building_id: number;
  year: number;
  consumption: number;
  emission: number;
  site_name?: string;
  building_name?: string;
  surface?: number;
  objectiveType?: string;
  importDate?: string;
  building?: string;
  objectiveValue?: number;
}

export interface CarbonObjectiveCreatePayload {
  site_id: number;
  sub_perimeter_id: number;
  year: number;
  emission: number; 
  carbon_objective_type?: string;
  declared?: boolean;
}

export interface CarbonObjectiveUpdatePayload {
  site_id: number;
  sub_perimeter_id: number; 
  year: number;
  emission: number; 
  carbon_objective_type?: string;
  declared?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EnergyObjectivesService {
  private _energyObjectives: { [key: string]: Observable<EnergyObjective[]> } = {};
  private _carbonObjectives: { [key: string]: Observable<CarbonObjective[]> } = {};

  constructor(
    private apiService: APIService,
  ) {}

  public getEnergyObjectives(
    perimeter: KPI.Perimeter,
    startYear: number,
    endYear: number
  ): Observable<EnergyObjective[]> {
    const key = this.getKey(arguments);
    if (!this._energyObjectives[key]) {
      this._energyObjectives[key] = this.apiService.get_energy_objectives_list({
        buildings: PerimetersService.getBuildingsIds([perimeter]).join(','),
        start_year: startYear,
        end_year: endYear
      }).pipe(
        map((response: any[]) => this.mapApiResponseToEnergyObjectives(response)),
        shareReplay(1)
      );
    }
    
    return this._energyObjectives[key];
  }

  public getEnergyObjectiveDetail(objectiveId: number): Observable<EnergyObjective> {
    return this.apiService.get_energy_objective_detail(objectiveId).pipe(
      map((response: any) => this.mapApiResponseToEnergyObjective(response))
    );
  }

  public createEnergyObjective(data: EnergyObjectiveCreatePayload): Observable<EnergyObjective> {
    return this.apiService.create_energy_objective(data).pipe(
      map((response: any) => this.mapApiResponseToEnergyObjective(response)),
      tap(() => this.clearCache())
    );
  }

  public updateEnergyObjective(objectiveId: number, data: EnergyObjectiveUpdatePayload): Observable<EnergyObjective> {
    return this.apiService.update_energy_objective(objectiveId, data).pipe(
      map((response: any) => this.mapApiResponseToEnergyObjective(response)),
      tap(() => this.clearCache())
    );
  }

  public deleteEnergyObjective(objectiveId: number): Observable<void> {
    return this.apiService.delete_energy_objective(objectiveId).pipe(
      tap(() => this.clearCache())
    );
  }

  public getCarbonObjectives(
    perimeter: KPI.Perimeter,
    startYear: number,
    endYear: number
  ): Observable<CarbonObjective[]> {
    const key = this.getKey(arguments);
    
    if (!this._carbonObjectives[key]) {
      this._carbonObjectives[key] = this.apiService.get_carbon_objectives_list({
        buildings: PerimetersService.getBuildingsIds([perimeter]).join(','),
        start_year: startYear,
        end_year: endYear
      }).pipe(
        map((response: any[]) => this.mapApiResponseToCarbonObjectives(response)),
        shareReplay(1)
      );
    }
    
    return this._carbonObjectives[key];
  }

  public createCarbonObjective(data: CarbonObjectiveCreatePayload): Observable<CarbonObjective> {
    return this.apiService.create_carbon_objective(data).pipe(
      map((response: any) => this.mapApiResponseToCarbonObjective(response)),
      tap(() => this.clearCache())
    );
  }

  public updateCarbonObjective(objectiveId: number, data: CarbonObjectiveUpdatePayload): Observable<CarbonObjective> {
    return this.apiService.update_carbon_objective(objectiveId, data).pipe(
      map((response: any) => this.mapApiResponseToCarbonObjective(response)),
      tap(() => this.clearCache())
    );
  }

  public deleteCarbonObjective(objectiveId: number): Observable<void> {
    return this.apiService.delete_carbon_objective(objectiveId).pipe(
      tap(() => this.clearCache())
    );
  }

  private clearCache(): void {
    Object.keys(this._energyObjectives).forEach(key => {
      delete this._energyObjectives[key];
    });
    Object.keys(this._carbonObjectives).forEach(key => {
      delete this._carbonObjectives[key];
    });
    this._energyObjectives = {};
    this._carbonObjectives = {};
  }

  private mapApiResponseToEnergyObjective(apiResponse: any): EnergyObjective {
    return {
      id: apiResponse.id,
      site_id: apiResponse.site_id,
      building_id: apiResponse.building_id,
      year: apiResponse.year,
      consumption: apiResponse.consumption,
      emission: apiResponse.emission,
      site_name: apiResponse.site_name || '',
      building_name: apiResponse.building_name || '',
      surface: apiResponse.surface || 0,
      objectiveType: apiResponse.objective_type || 'Objectif kWh site',
      importDate: apiResponse.import_date || new Date().toLocaleDateString(),
      building: apiResponse.building_name || apiResponse.building || '',
      objectiveValue: apiResponse.consumption || apiResponse.objective_value || 0
    };
  }

  private mapApiResponseToEnergyObjectives(apiResponse: any[]): EnergyObjective[] {
    const energyObjectives: EnergyObjective[] = [];
    
    apiResponse.forEach(siteData => {
      const siteId = siteData.site?.id;
      const siteName = siteData.site?.name || '';
      
      siteData.monosite_objectives?.forEach(monoObjective => {
        const buildingId = monoObjective.mono_perimeter?.id;
        const buildingName = monoObjective.mono_perimeter?.name || '';
        const area = monoObjective.area || 0;
        
        Object.keys(monoObjective.objectives || {}).forEach(yearStr => {
          const year = parseInt(yearStr);
          const yearData = monoObjective.objectives[yearStr];
          if (yearData && yearData.consumption !== 0) {
            const objective: EnergyObjective = {
              id: yearData.id, 
              site_id: siteId,
              building_id: buildingId,
              year: year,
              consumption: yearData.consumption,
              emission: yearData.emission || 0,
              site_name: siteName,
              building_name: buildingName,
              surface: area,
              objectiveType: yearData.energy_objective_type || 'Objectif kWh site',
              importDate: new Date().toLocaleDateString(),
              building: buildingName,
              objectiveValue: yearData.consumption
            };
            energyObjectives.push(objective);
          }
        });
      });
    });
    
    return energyObjectives;
  }

  private mapApiResponseToCarbonObjective(apiResponse: any): CarbonObjective {
    return {
      id: apiResponse.id,
      site_id: apiResponse.site_id,
      building_id: apiResponse.building_id,
      year: apiResponse.year,
      consumption: apiResponse.consumption || 0,
      emission: apiResponse.emission,
      site_name: apiResponse.site_name || '',
      building_name: apiResponse.building_name || '',
      surface: apiResponse.surface || 0,
      objectiveType: apiResponse.objective_type || 'Objectif carbone personnalisé pour le site',
      importDate: apiResponse.import_date || new Date().toLocaleDateString(),
      building: apiResponse.building_name || apiResponse.building || '',
      objectiveValue: apiResponse.emission || apiResponse.objective_value || 0
    };
  }

  private mapApiResponseToCarbonObjectives(apiResponse: any[]): CarbonObjective[] {
    const carbonObjectives: CarbonObjective[] = [];
    
    apiResponse.forEach(siteData => {
      const siteId = siteData.site?.id;
      const siteName = siteData.site?.name || '';

      siteData.monosite_objectives?.forEach(monoObjective => {
        const buildingId = monoObjective.mono_perimeter?.id;
        const buildingName = monoObjective.mono_perimeter?.name || '';
        const area = monoObjective.area || 0;
        
        Object.keys(monoObjective.objectives || {}).forEach(yearStr => {
          const year = parseInt(yearStr);
          const yearData = monoObjective.objectives[yearStr];
          
          if (yearData && yearData.emission !== 0) {
            const objective: CarbonObjective = {
              id: yearData.id,
              site_id: siteId,
              building_id: buildingId,
              year: year,
              consumption: yearData.consumption || 0,
              emission: yearData.emission,
              site_name: siteName,
              building_name: buildingName,
              surface: area,
              objectiveType: yearData.carbon_objective_type || 'Objectif carbone site',
              importDate: new Date().toLocaleDateString(),
              building: buildingName,
              objectiveValue: yearData.emission
            };
            
            carbonObjectives.push(objective);
          }
        });
      });
    });
    
    return carbonObjectives;
  }

  public getObjectivesForBuilding(buildingId: number): Observable<EnergyObjective[]> {
    return this._energyObjectives[Object.keys(this._energyObjectives)[0]]?.pipe(
      map(objectives => objectives.filter(obj => obj.building_id === buildingId))
    ) || new BehaviorSubject<EnergyObjective[]>([]).asObservable();
  }

  public getObjectivesForYear(year: number): Observable<EnergyObjective[]> {
    return this._energyObjectives[Object.keys(this._energyObjectives)[0]]?.pipe(
      map(objectives => objectives.filter(obj => obj.year === year))
    ) || new BehaviorSubject<EnergyObjective[]>([]).asObservable();
  }

  public getObjectivesForSite(siteId: number): Observable<EnergyObjective[]> {
    return this._energyObjectives[Object.keys(this._energyObjectives)[0]]?.pipe(
      map(objectives => objectives.filter(obj => obj.site_id === siteId))
    ) || new BehaviorSubject<EnergyObjective[]>([]).asObservable();
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }
}
